<!-- src/views/OnBoarding/PrimarySponsorContractFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Primary Sponsor Contract</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Legal Sponsorship Agreement • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Legal Document</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <form @submit.prevent="submitForm" class="space-y-16">
            
            <!-- Section 1: Personnel Verification -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Your Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Full Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.name || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Verified Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Employment Type</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight capitalize">
                    {{ (userProfile.employmentType || 'Loading...').replace('_', '-') }}
                  </p>
                </div>
              </div>
            </section>

            <!-- Section 2: Contract Document -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <DocumentTextIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Contractual Agreement</h2>
              </div>

              <div class="p-8 bg-slate-50 border border-slate-200 rounded-[32px] shadow-inner">
                <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2 py-10 justify-center">
                   <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
                   <span class="font-black uppercase text-xs tracking-widest">Fetching Contract Terms...</span>
                </div>
                <div v-else class="ql-editor !p-0 text-slate-700" v-html="contractText"></div>
              </div>
            </section>

            <!-- Section 3: Confirmation -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
                <div class="absolute top-0 right-0 p-10 opacity-5">
                  <ShieldCheckIcon class="h-32 w-32" />
                </div>
                <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div class="max-w-xl">
                      <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">3. Legal Acknowledgement</h2>
                      <p class="text-slate-400 font-medium leading-relaxed">By ticking this box, you formally confirm that you have read, understood, and agreed to the Primary Sponsor Contract.</p>
                    </div>
                    <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                        <div class="flex items-center">
                            <input id="confirmation" type="checkbox" v-model="formData.confirmation" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent transition-all hover:border-[#20FCF1]">
                            <label for="confirmation" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">Accept & Bind<br>Agreement <span class="text-red-500">*</span></label>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Section 4: Signature -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Digital Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6">Profile Linked Digital Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Verified Signature</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-bold uppercase tracking-tight border border-red-100">
                                Profile Signature Not Found
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">
                                Update Profile First
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <div class="pt-10">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="btn-premium">
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Processing Contract...
                </span>
                <span v-else>Submit Primary Sponsor Contract</span>
              </button>
            </div>
          </form>

          <p v-if="successMessage" class="mt-8 p-6 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce font-black text-brand-primary uppercase tracking-widest">{{ successMessage }}</p>
          <p v-if="errorMessage" class="mt-8 p-6 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  DocumentTextIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon, 
  CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ name: '', email: '', employmentType: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({ confirmation: false });
const contractText = ref('');
const isLoadingText = ref(true);
const isSubmitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const isFormValid = computed(() => formData.confirmation && (userProfile.signatureUrl || userProfile.signatureDataUrl) && !isLoadingText.value);

const fetchPrerequisites = async () => {
  if (!user.value) {
    errorMessage.value = "Authentication not found. Please log in again.";
    isLoadingText.value = false;
    return;
  }

  try {
    isLoadingText.value = true;
    
    // 1. Get Claims for Client ID
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;
    if (!clientId) throw new Error("Client association missing.");

    // 2. Load User Profile and Client Document
    const userProfileRef = doc(db, 'userProfiles', user.value.uid);
    const clientDocRef = doc(db, 'clients', clientId);

    const [userProfileSnap, clientDocSnap] = await Promise.all([
      getDoc(userProfileRef),
      getDoc(clientDocRef)
    ]);

    if (userProfileSnap.exists()) {
      const profile = userProfileSnap.data();
      userProfile.name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      userProfile.email = profile.email || user.value.email;
      userProfile.employmentType = profile.employmentType || 'Not specified';
      userProfile.signatureUrl = profile.signatureUrl || '';
      userProfile.signatureDataUrl = profile.signatureDataUrl || '';
    } else {
      throw new Error("User profile not found. Please complete 'Your Details' first.");
    }

    if (clientDocSnap.exists()) {
      const clientData = clientDocSnap.data();
      contractText.value = clientData.onboardingFormTexts?.primarySponsorContract || '';
    }

  } catch (error) {
    errorMessage.value = `Error loading prerequisites: ${error.message}`;
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(fetchPrerequisites);

const submitForm = async () => {
  if (!isFormValid.value) {
    errorMessage.value = "Please complete all required fields.";
    return;
  }
  
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  const submissionData = {
    ...formData,
    name: userProfile.name,
    email: userProfile.email,
    employmentType: userProfile.employmentType,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    contractText: contractText.value,
    status: 'completed',
  };

  try {
    const submitFormFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFormFunction({
      formIdentifier: 'primarySponsorContract',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
      successMessage.value = `Contract submitted successfully! Reference: ${result.data.referenceNumber}`;
      setTimeout(() => router.push('/onboarding-forms'), 2000);
    } else {
      throw new Error(result.data.error || 'The form submission failed.');
    }
  } catch (error) {
    errorMessage.value = `Failed to submit form. ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; 
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2), :deep(.ql-editor h3) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>